import { forwardRef, useEffect } from "react";
import {
  useForm,
  Controller,
  type ControllerRenderProps,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styles from "./TopUpView.module.scss";
import autoTopUpStyles from "./AutoTopUpForm.module.scss";
import { cn } from "@/shared/lib/utils";
import {
  autoTopUpSchema,
  type AutoTopUpFormValues,
} from "@/modules/Balance/model/auto-top-up-schema";
import type { AutoTopUpInfo, EnableAutoTopUpRequest } from "@/shared/api/types";
import { useEnableAutoTopUp } from "@/modules/Balance/hooks/use-enable-auto-top-up";
import { useTopUpStore } from "@/modules/Balance/model/top-up-store";
import { useToastStore } from "@/shared/stores/toastStore";

interface AutoTopUpFormProps {
  autoTopUpInfo: AutoTopUpInfo | null | undefined;
  selectedCardId: string | null;
  email: string;
}

const suggestedAmounts = [1000, 3000, 5000, 10000];

export const AutoTopUpForm = forwardRef<HTMLDivElement, AutoTopUpFormProps>(
  ({ autoTopUpInfo, selectedCardId, email }, ref) => {
    const { autoTopUpId } = useTopUpStore();
    const { mutate: enableAutoTopUp, isPending } = useEnableAutoTopUp();
    const { showToast } = useToastStore();

    const {
      control,
      handleSubmit,
      setValue,
      formState: { errors },
    } = useForm<AutoTopUpFormValues>({
      resolver: zodResolver(autoTopUpSchema),
      mode: "onSubmit",
      defaultValues: {
        threshold: autoTopUpInfo?.threshold_amount
          ? String(autoTopUpInfo.threshold_amount / 100)
          : "",
        amount: autoTopUpInfo?.top_up_amount
          ? String(autoTopUpInfo.top_up_amount / 100)
          : "",
        paymentMethodId: selectedCardId,
        email: email,
      },
    });

    useEffect(() => {
      setValue("paymentMethodId", selectedCardId, { shouldValidate: true });
    }, [selectedCardId, setValue]);

    useEffect(() => {
      setValue("email", email, { shouldValidate: true });
    }, [email, setValue]);

    const onSubmit = (data: AutoTopUpFormValues) => {
      if (!autoTopUpId || !data.paymentMethodId) {
        showToast(
          "Ошибка: Не удалось определить ID настройки или способ оплаты.",
          "error",
        );
        return;
      }

      const requestData: EnableAutoTopUpRequest = {
        threshold_amount: Number(data.threshold) * 100,
        top_up_amount: Number(data.amount) * 100,
        payment_method_id: data.paymentMethodId,
        receipt_email: data.email,
      };

      enableAutoTopUp(
        { autoTopUpId: autoTopUpId, data: requestData },
        {
          onSuccess: () => {
            showToast("Настройки автопополнения сохранены", "success");
          },
          onError: (error) => {
            showToast(error.message, "error");
          },
        },
      );
    };

    return (
      <div ref={ref} className={cn(autoTopUpStyles.autoTopUpFormContainer)}>
        <h2 className={autoTopUpStyles.title}>Настройка автопополнения</h2>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={autoTopUpStyles.form}
        >
          <div className={styles.paymentInputWrap}>
            <Controller
              name="threshold"
              control={control}
              render={({
                field,
              }: {
                field: ControllerRenderProps<AutoTopUpFormValues, "threshold">;
              }) => (
                <div className={styles.paymentInputGroup}>
                  <div className={styles.paymentInputLabel}>
                    При балансе ниже, ₽
                  </div>
                  <input
                    {...field}
                    type="number"
                    className={`${styles.paymentInput} ${styles.labeledPaymentInput}`}
                    placeholder="1 000"
                  />
                </div>
              )}
            />
            {errors.threshold && (
              <p className={styles.errorMessage}>{errors.threshold.message}</p>
            )}
          </div>

          <div className={styles.paymentInputWrap}>
            <div className={styles.paymentInputSum}>
              <Controller
                name="amount"
                control={control}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<AutoTopUpFormValues, "amount">;
                }) => (
                  <div className={styles.paymentInputGroup}>
                    <div className={styles.paymentInputLabel}>
                      Пополнять на сумму, ₽
                    </div>
                    <input
                      {...field}
                      type="number"
                      className={`${styles.paymentInput} ${styles.labeledPaymentInput}`}
                      placeholder="1 000"
                    />
                  </div>
                )}
              />
              <div className={styles.quickAmounts}>
                {suggestedAmounts.map((suggested) => (
                  <button
                    type="button"
                    key={suggested}
                    className={styles.quickAmountButton}
                    onClick={() =>
                      setValue("amount", String(suggested), {
                        shouldValidate: true,
                      })
                    }
                  >
                    {suggested.toLocaleString("ru-RU")} ₽
                  </button>
                ))}
              </div>
            </div>
            {errors.amount && (
              <p className={styles.errorMessage}>{errors.amount.message}</p>
            )}
          </div>
          {errors.paymentMethodId && (
            <p className={styles.errorMessage}>
              {errors.paymentMethodId.message}
            </p>
          )}
          {errors.email && (
            <p className={styles.errorMessage}>{errors.email.message}</p>
          )}

          <div className={styles.buttons}>
            <button
              type="submit"
              className={styles.confirmButton}
              disabled={isPending}
            >
              {isPending ? <div className={styles.spinner}></div> : "Сохранить"}
            </button>
          </div>
        </form>
      </div>
    );
  },
);
